import { useState, useEffect, useCallback, useMemo } from 'react';
import Head from 'next/head';
import Layout from '@/components/Layout';
import { useAuth } from '@/lib/authContext';
import { supabase } from '@/lib/supabase';
import toast from 'react-hot-toast';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';

// ─── Icon helper ────────────────────────────────────────────

function Icon({ name, className }: { name: string; className?: string }) {
  return (
    <span className={cn('material-symbols-outlined', className)}>{name}</span>
  );
}

// ─── Types ───────────────────────────────────────────────────

interface Proveedor {
  id: string;
  asesor_id: string;
  cif: string;
  nombre: string;
  referencia: string;
  tipo: string;
  cuenta_proveedor: string;
  nombre_comercial: string;
  direccion: string;
  cp: string;
  ciudad: string;
  provincia: string;
  pais: string;
  email: string;
  telefono: string;
  website: string;
  notas: string;
  // Parámetros
  cuentas_gastos: string;
  pago_automatico: string;
  cuenta_pago: string;
  tipo_retencion: string;
  separador_decimal: string;
  autorrellenar_vencimiento: string;
  total_factura_minimo: string;
  total_factura_maximo: string;
  actividad_defecto: string;
  cuenta_suplidos: string;
  recortar_factura: string;
  caracteres_mantener: string;
  caracteres_excluir: string;
  ignorar_validacion_cif: boolean;
  inversion_sujeto_pasivo_iva: boolean;
  inversion_sujeto_pasivo_igic: boolean;
  intracomunitario: boolean;
  no_sujeta: boolean;
  tipo_cero: boolean;
  deducibilidad: string;
  created_at?: string;
  updated_at?: string;
}

type ProveedorFormData = Omit<Proveedor, 'id' | 'created_at' | 'updated_at'>;

const EMPTY_PROVEEDOR: ProveedorFormData = {
  asesor_id: '',
  cif: '',
  nombre: '',
  referencia: '',
  tipo: '',
  cuenta_proveedor: '',
  nombre_comercial: '',
  direccion: '',
  cp: '',
  ciudad: '',
  provincia: '',
  pais: '',
  email: '',
  telefono: '',
  website: '',
  notas: '',
  cuentas_gastos: '',
  pago_automatico: 'N/A',
  cuenta_pago: '',
  tipo_retencion: 'N/A',
  separador_decimal: 'default',
  autorrellenar_vencimiento: 'N/A',
  total_factura_minimo: '',
  total_factura_maximo: '',
  actividad_defecto: '',
  cuenta_suplidos: '',
  recortar_factura: 'N/A',
  caracteres_mantener: '',
  caracteres_excluir: '',
  ignorar_validacion_cif: false,
  inversion_sujeto_pasivo_iva: false,
  inversion_sujeto_pasivo_igic: false,
  intracomunitario: false,
  no_sujeta: false,
  tipo_cero: false,
  deducibilidad: 'deducible',
};

const PAISES = [
  'España', 'Portugal', 'Francia', 'Alemania', 'Italia', 'Reino Unido',
  'Países Bajos', 'Bélgica', 'Suiza', 'Austria', 'Irlanda', 'Suecia',
  'Noruega', 'Dinamarca', 'Finlandia', 'Polonia', 'República Checa',
  'Grecia', 'Rumanía', 'Hungría', 'Estados Unidos', 'México',
  'Argentina', 'Brasil', 'Chile', 'Colombia', 'Otro',
];

const TIPOS_PROVEEDOR = [
  { value: 'nacional', label: 'Nacional' },
  { value: 'intracomunitario', label: 'Intracomunitario' },
  { value: 'extracomunitario', label: 'Extracomunitario' },
  { value: 'autonomo', label: 'Autónomo' },
];

const PAGE_SIZE = 15;

// ─── Component ───────────────────────────────────────────────

export default function AsesorProveedores() {
  const { user } = useAuth();

  // ── State ──
  const [view, setView] = useState<'list' | 'edit'>('list');
  const [proveedores, setProveedores] = useState<Proveedor[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState<'datos' | 'parametros'>('datos');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<ProveedorFormData>({ ...EMPTY_PROVEEDOR });
  const [useLocalState, setUseLocalState] = useState(false);

  // ── Data Loading ──
  const loadProveedores = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);

    try {
      const { data, error } = await supabase
        .from('proveedores')
        .select('*')
        .eq('asesor_id', user.id)
        .order('nombre', { ascending: true });

      if (error) {
        // Table might not exist yet — fall back to local state
        console.warn('Supabase proveedores table not available, using local state:', error.message);
        setUseLocalState(true);
      } else {
        setProveedores(data || []);
        setUseLocalState(false);
      }
    } catch {
      console.warn('Could not connect to Supabase, using local state');
      setUseLocalState(true);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    loadProveedores();
  }, [loadProveedores]);

  // ── Filtering + Pagination ──
  const filtered = useMemo(() => {
    if (!searchTerm.trim()) return proveedores;
    const term = searchTerm.toLowerCase();
    return proveedores.filter(
      (p) =>
        p.nombre.toLowerCase().includes(term) ||
        p.cif.toLowerCase().includes(term) ||
        p.cuenta_proveedor.toLowerCase().includes(term) ||
        p.cuentas_gastos.toLowerCase().includes(term)
    );
  }, [proveedores, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = useMemo(
    () => filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [filtered, currentPage]
  );

  // Reset page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // ── CRUD ──
  const handleNew = () => {
    setEditingId(null);
    setFormData({ ...EMPTY_PROVEEDOR, asesor_id: user?.id || '' });
    setActiveTab('datos');
    setView('edit');
  };

  const handleRowClick = (proveedor: Proveedor) => {
    setEditingId(proveedor.id);
    const { id, created_at, updated_at, ...rest } = proveedor;
    setFormData(rest);
    setActiveTab('datos');
    setView('edit');
  };

  const handleBackToList = () => {
    setView('list');
    setEditingId(null);
    setFormData({ ...EMPTY_PROVEEDOR });
  };

  const handleSave = async () => {
    if (!formData.cif.trim() || !formData.nombre.trim()) {
      toast.error('CIF y Nombre son campos obligatorios');
      return;
    }

    setSaving(true);
    try {
      if (useLocalState) {
        // Local-only mode
        if (editingId) {
          setProveedores((prev) =>
            prev.map((p) =>
              p.id === editingId
                ? { ...p, ...formData, updated_at: new Date().toISOString() }
                : p
            )
          );
        } else {
          const newProveedor: Proveedor = {
            ...formData,
            id: crypto.randomUUID(),
            asesor_id: user?.id || '',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          };
          setProveedores((prev) => [...prev, newProveedor]);
        }
        toast.success(editingId ? 'Proveedor actualizado' : 'Proveedor creado');
        handleBackToList();
        return;
      }

      // Supabase mode
      const payload = { ...formData, asesor_id: user?.id || '' };

      if (editingId) {
        const { error } = await supabase
          .from('proveedores')
          .update(payload)
          .eq('id', editingId);
        if (error) throw error;
        toast.success('Proveedor actualizado correctamente');
      } else {
        const { error } = await supabase
          .from('proveedores')
          .insert(payload);
        if (error) throw error;
        toast.success('Proveedor creado correctamente');
      }

      await loadProveedores();
      handleBackToList();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error desconocido';
      toast.error(`Error al guardar: ${message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editingId) return;
    const confirmed = window.confirm('¿Estás seguro de que deseas eliminar este proveedor?');
    if (!confirmed) return;

    setSaving(true);
    try {
      if (useLocalState) {
        setProveedores((prev) => prev.filter((p) => p.id !== editingId));
        toast.success('Proveedor eliminado');
        handleBackToList();
        return;
      }

      const { error } = await supabase
        .from('proveedores')
        .delete()
        .eq('id', editingId);
      if (error) throw error;

      toast.success('Proveedor eliminado correctamente');
      await loadProveedores();
      handleBackToList();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error desconocido';
      toast.error(`Error al eliminar: ${message}`);
    } finally {
      setSaving(false);
    }
  };

  // ── Form Helpers ──
  const updateField = <K extends keyof ProveedorFormData>(
    field: K,
    value: ProveedorFormData[K]
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleRefresh = () => {
    loadProveedores();
    toast.success('Datos actualizados');
  };

  // ── Loading State ──
  if (loading) {
    return (
      <Layout rol="asesor">
        <Head>
          <title>Proveedores — Externaliza2</title>
        </Head>
        <div className="px-12 pb-12 pt-8 space-y-12">
          <div className="space-y-3">
            <Skeleton className="h-12 w-72" />
            <Skeleton className="h-4 w-80" />
          </div>
          <Skeleton className="h-12 rounded-huge" />
          <Skeleton className="h-[400px] rounded-huge" />
        </div>
      </Layout>
    );
  }

  // ─── LIST VIEW ─────────────────────────────────────────────
  if (view === 'list') {
    return (
      <Layout rol="asesor">
        <Head>
          <title>Proveedores — Externaliza2</title>
        </Head>
        <div className="h-[calc(100vh-72px)] overflow-y-auto scrollbar-thin">
          <div className="px-12 pb-12 space-y-12">

            {/* ──────── Header ──────── */}
            <div className="flex items-end justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-calm-accent mb-2">Gestión</p>
                <h1 className="text-5xl font-display font-light text-calm-text">Proveedores</h1>
                <p className="text-calm-muted font-light mt-2">
                  {filtered.length} {filtered.length === 1 ? 'proveedor' : 'proveedores'} registrados
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleRefresh}
                  className="border border-calm-border rounded-huge w-10 h-10 flex items-center justify-center text-calm-muted hover:bg-white hover:text-calm-text transition-all"
                  title="Actualizar"
                >
                  <Icon name="refresh" className="text-lg" />
                </button>
                <button
                  onClick={handleNew}
                  className="border border-calm-border rounded-huge px-5 py-2.5 text-sm font-light text-calm-muted hover:bg-white hover:text-calm-text transition-all flex items-center gap-2"
                >
                  <Icon name="add" className="text-lg" />
                  Nuevo proveedor
                </button>
              </div>
            </div>

            {/* ──────── Search Bar ──────── */}
            <div className="relative max-w-md">
              <Icon name="search" className="absolute left-3 top-1/2 -translate-y-1/2 text-calm-accent pointer-events-none text-lg" />
              <Input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar por nombre, CIF, cuenta…"
                className="pl-10 border-calm-border rounded-2xl text-sm font-light"
              />
              {searchTerm && (
                <button
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-calm-accent hover:text-calm-text transition-colors"
                  onClick={() => setSearchTerm('')}
                >
                  <Icon name="close" className="text-base" />
                </button>
              )}
            </div>

            {/* ──────── Table ──────── */}
            <div className="bg-gradient-to-br from-white to-fresh-sky-50/20 border border-fresh-sky-200/40 rounded-huge overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-calm-border">
                    <TableHead className="p-4 text-[11px] uppercase tracking-widest text-calm-accent font-normal">
                      Nombre
                    </TableHead>
                    <TableHead className="p-4 text-[11px] uppercase tracking-widest text-calm-accent font-normal">
                      CIF
                    </TableHead>
                    <TableHead className="p-4 text-[11px] uppercase tracking-widest text-calm-accent font-normal hidden md:table-cell">
                      Cuentas de Gastos
                    </TableHead>
                    <TableHead className="p-4 text-[11px] uppercase tracking-widest text-calm-accent font-normal hidden lg:table-cell">
                      Cuenta de Proveedor
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginated.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="px-4 py-16 text-center">
                        <div className="flex flex-col items-center gap-3">
                          <Icon name="store" className="text-[32px] text-calm-accent" />
                          <div>
                            <p className="text-calm-text font-light">
                              {searchTerm
                                ? 'No se encontraron resultados'
                                : 'Sin proveedores'}
                            </p>
                            <p className="text-calm-accent text-sm font-light mt-1">
                              {searchTerm
                                ? 'Prueba con otro término de búsqueda'
                                : 'Añade tu primer proveedor para empezar'}
                            </p>
                          </div>
                          {!searchTerm && (
                            <button
                              onClick={handleNew}
                              className="mt-2 border border-calm-border rounded-huge px-4 py-2 text-sm font-light text-calm-muted hover:bg-white hover:text-calm-text transition-all flex items-center gap-2"
                            >
                              <Icon name="add" className="text-base" />
                              Añadir proveedor
                            </button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginated.map((prov) => (
                      <TableRow
                        key={prov.id}
                        onClick={() => handleRowClick(prov)}
                        className="cursor-pointer hover:bg-slate-50/50 transition-colors border-b border-calm-border/50 last:border-b-0"
                      >
                        <TableCell className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-xl bg-slate-50 flex items-center justify-center text-calm-accent flex-shrink-0">
                              <Icon name="store" className="text-base" />
                            </div>
                            <div className="min-w-0">
                              <p className="font-light text-calm-text truncate text-sm">
                                {prov.nombre}
                              </p>
                              {prov.nombre_comercial && (
                                <p className="text-xs text-calm-accent font-light truncate">
                                  {prov.nombre_comercial}
                                </p>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="p-4">
                          <span className="font-mono text-sm font-light text-calm-muted">
                            {prov.cif}
                          </span>
                        </TableCell>
                        <TableCell className="p-4 hidden md:table-cell">
                          <span className="font-mono text-sm font-light text-calm-muted">
                            {prov.cuentas_gastos || '—'}
                          </span>
                        </TableCell>
                        <TableCell className="p-4 hidden lg:table-cell">
                          <span className="font-mono text-sm font-light text-calm-muted">
                            {prov.cuenta_proveedor || '—'}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* ──────── Pagination ──────── */}
            {totalPages > 1 && (
              <div className="flex items-center justify-end gap-6 text-sm font-light text-calm-muted">
                <span className="tabular-nums whitespace-nowrap">
                  {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filtered.length)} de {filtered.length}
                </span>
                <div className="flex gap-1">
                  <button
                    className="border border-calm-border rounded-huge w-8 h-8 flex items-center justify-center text-calm-muted hover:bg-white hover:text-calm-text transition-all disabled:opacity-40"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    aria-label="Página anterior"
                  >
                    <Icon name="chevron_left" className="text-base" />
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter((page) => {
                      if (totalPages <= 7) return true;
                      if (page === 1 || page === totalPages) return true;
                      return Math.abs(page - currentPage) <= 1;
                    })
                    .map((page, idx, arr) => {
                      const showEllipsis = idx > 0 && page - arr[idx - 1] > 1;
                      return (
                        <span key={page} className="flex items-center">
                          {showEllipsis && (
                            <span className="px-1.5 text-calm-accent text-sm">…</span>
                          )}
                          <button
                            className={cn(
                              'rounded-huge w-8 h-8 flex items-center justify-center text-sm transition-all',
                              page === currentPage
                                ? 'bg-calm-text text-white'
                                : 'text-calm-muted hover:bg-white hover:text-calm-text border border-transparent hover:border-calm-border'
                            )}
                            onClick={() => setCurrentPage(page)}
                          >
                            {page}
                          </button>
                        </span>
                      );
                    })}
                  <button
                    className="border border-calm-border rounded-huge w-8 h-8 flex items-center justify-center text-calm-muted hover:bg-white hover:text-calm-text transition-all disabled:opacity-40"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    aria-label="Página siguiente"
                  >
                    <Icon name="chevron_right" className="text-base" />
                  </button>
                </div>
              </div>
            )}

          </div>
        </div>
      </Layout>
    );
  }

  // ─── EDIT / CREATE VIEW ────────────────────────────────────
  return (
    <Layout rol="asesor">
      <Head>
        <title>
          {editingId ? `${formData.nombre || 'Proveedor'} - Editar` : 'Nuevo Proveedor'} — Externaliza2
        </title>
      </Head>
      <div className="h-[calc(100vh-72px)] overflow-y-auto scrollbar-thin">
        <div className="px-12 pb-12 space-y-12">

          {/* ──────── Header ──────── */}
          <div className="flex items-end justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={handleBackToList}
                className="border border-calm-border rounded-huge w-10 h-10 flex items-center justify-center text-calm-muted hover:bg-white hover:text-calm-text transition-all"
                title="Volver a la lista"
              >
                <Icon name="arrow_back" className="text-lg" />
              </button>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-calm-accent mb-1">
                  {editingId ? 'Editar proveedor' : 'Nuevo registro'}
                </p>
                <h1 className="text-3xl font-display font-light text-calm-text">
                  {editingId ? formData.nombre || 'Editar Proveedor' : 'Nuevo Proveedor'}
                </h1>
                {editingId && formData.cif && (
                  <p className="text-calm-accent text-sm font-mono font-light mt-1">{formData.cif}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              {editingId && (
                <button
                  onClick={handleDelete}
                  disabled={saving}
                  className="border border-red-200 rounded-huge px-4 py-2.5 text-sm font-light text-red-500 hover:bg-red-50 transition-all flex items-center gap-2 disabled:opacity-40"
                  title="Eliminar proveedor"
                >
                  <Icon name="delete" className="text-base" />
                  <span className="hidden sm:inline">Eliminar</span>
                </button>
              )}
              <button
                onClick={handleSave}
                disabled={saving}
                className="border border-calm-border rounded-huge px-5 py-2.5 text-sm font-light text-calm-muted hover:bg-white hover:text-calm-text transition-all flex items-center gap-2 disabled:opacity-40"
              >
                <Icon name="save" className="text-base" />
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </div>

          {/* ──────── Tabs ──────── */}
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'datos' | 'parametros')}>
            <TabsList className="bg-white border border-calm-border rounded-huge p-1">
              <TabsTrigger
                value="datos"
                className="gap-2 rounded-huge text-sm font-light data-[state=active]:bg-calm-bg"
              >
                <Icon name="description" className="text-base" />
                Datos
              </TabsTrigger>
              <TabsTrigger
                value="parametros"
                className="gap-2 rounded-huge text-sm font-light data-[state=active]:bg-calm-bg"
              >
                <Icon name="tune" className="text-base" />
                Parámetros
              </TabsTrigger>
            </TabsList>

            {/* ─── Tab: Datos ─── */}
            <TabsContent value="datos">
              <div className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge p-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-5">
                  {/* Row 1 */}
                  <FormField label="CIF" required>
                    <Input
                      type="text"
                      value={formData.cif}
                      onChange={(e) => updateField('cif', e.target.value.toUpperCase())}
                      className="font-mono border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light"
                      placeholder="B12345678"
                    />
                  </FormField>
                  <FormField label="Nombre" required>
                    <Input
                      type="text"
                      value={formData.nombre}
                      onChange={(e) => updateField('nombre', e.target.value)}
                      className="border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light"
                      placeholder="Razón social del proveedor"
                    />
                  </FormField>
                  <FormField label="Referencia">
                    <Input
                      type="text"
                      value={formData.referencia}
                      onChange={(e) => updateField('referencia', e.target.value)}
                      className="border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light"
                      placeholder="Referencia interna"
                    />
                  </FormField>

                  {/* Row 2 */}
                  <FormField label="Tipo">
                    <Select value={formData.tipo || undefined} onValueChange={(v) => updateField('tipo', v)}>
                      <SelectTrigger className="border-calm-border rounded-2xl text-sm font-light">
                        <SelectValue placeholder="Seleccionar…" />
                      </SelectTrigger>
                      <SelectContent>
                        {TIPOS_PROVEEDOR.map((t) => (
                          <SelectItem key={t.value} value={t.value}>
                            {t.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormField>
                  <FormField label="Cuenta de proveedor">
                    <Input
                      type="text"
                      value={formData.cuenta_proveedor}
                      onChange={(e) => updateField('cuenta_proveedor', e.target.value)}
                      className="font-mono border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light"
                      placeholder="4000000000"
                    />
                  </FormField>
                  <div /> {/* spacer */}

                  {/* Row 3 */}
                  <FormField label="Nombre Comercial">
                    <Input
                      type="text"
                      value={formData.nombre_comercial}
                      onChange={(e) => updateField('nombre_comercial', e.target.value)}
                      className="border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light"
                      placeholder="Nombre comercial"
                    />
                  </FormField>
                  <FormField label="Dirección">
                    <Input
                      type="text"
                      value={formData.direccion}
                      onChange={(e) => updateField('direccion', e.target.value)}
                      className="border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light"
                      placeholder="Calle, número…"
                    />
                  </FormField>
                  <FormField label="CP">
                    <Input
                      type="text"
                      value={formData.cp}
                      onChange={(e) => updateField('cp', e.target.value)}
                      className="border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light"
                      placeholder="28001"
                      maxLength={10}
                    />
                  </FormField>

                  {/* Row 4 */}
                  <FormField label="Ciudad">
                    <Input
                      type="text"
                      value={formData.ciudad}
                      onChange={(e) => updateField('ciudad', e.target.value)}
                      className="border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light"
                      placeholder="Ciudad"
                    />
                  </FormField>
                  <FormField label="Provincia">
                    <Input
                      type="text"
                      value={formData.provincia}
                      onChange={(e) => updateField('provincia', e.target.value)}
                      className="border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light"
                      placeholder="Provincia"
                    />
                  </FormField>
                  <FormField label="País">
                    <Select value={formData.pais || undefined} onValueChange={(v) => updateField('pais', v)}>
                      <SelectTrigger className="border-calm-border rounded-2xl text-sm font-light">
                        <SelectValue placeholder="Seleccionar país…" />
                      </SelectTrigger>
                      <SelectContent>
                        {PAISES.map((p) => (
                          <SelectItem key={p} value={p}>
                            {p}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormField>

                  {/* Row 5 */}
                  <FormField label="Email">
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => updateField('email', e.target.value)}
                      className="border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light"
                      placeholder="contacto@proveedor.com"
                    />
                  </FormField>
                  <FormField label="Teléfono">
                    <Input
                      type="tel"
                      value={formData.telefono}
                      onChange={(e) => updateField('telefono', e.target.value)}
                      className="border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light"
                      placeholder="+34 600 000 000"
                    />
                  </FormField>
                  <FormField label="Website">
                    <Input
                      type="url"
                      value={formData.website}
                      onChange={(e) => updateField('website', e.target.value)}
                      className="border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light"
                      placeholder="https://www.ejemplo.com"
                    />
                  </FormField>

                  {/* Row 6 — full width */}
                  <div className="md:col-span-3">
                    <FormField label="Notas">
                      <Textarea
                        value={formData.notas}
                        onChange={(e) => updateField('notas', e.target.value)}
                        rows={4}
                        className="border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light"
                        placeholder="Observaciones o notas adicionales sobre este proveedor…"
                      />
                    </FormField>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* ─── Tab: Parámetros ─── */}
            <TabsContent value="parametros">
              <div className="space-y-8">
                {/* Main parameters grid */}
                <div className="bg-gradient-to-br from-white to-slate-50 border border-slate-200/60 rounded-huge p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <Icon name="account_balance" className="text-calm-accent text-lg" />
                    <h3 className="text-sm font-display font-light text-calm-text">Configuración Contable</h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
                    {/* Left Column */}
                    <FormField label="Cuentas de gastos">
                      <Input
                        type="text"
                        value={formData.cuentas_gastos}
                        onChange={(e) => updateField('cuentas_gastos', e.target.value)}
                        className="font-mono border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light"
                        placeholder="6000000000"
                      />
                    </FormField>

                    <FormField label="Cuenta de Pago">
                      <Input
                        type="text"
                        value={formData.cuenta_pago}
                        onChange={(e) => updateField('cuenta_pago', e.target.value)}
                        className="font-mono border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light"
                        placeholder="Cuenta de pago"
                      />
                    </FormField>

                    <FormField label="Pago automático">
                      <Select value={formData.pago_automatico} onValueChange={(v) => updateField('pago_automatico', v)}>
                        <SelectTrigger className="border-calm-border rounded-2xl text-sm font-light">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="N/A">N/A</SelectItem>
                          <SelectItem value="si">Sí</SelectItem>
                          <SelectItem value="no">No</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormField>

                    <FormField label="Tipo de Retención">
                      <Select value={formData.tipo_retencion} onValueChange={(v) => updateField('tipo_retencion', v)}>
                        <SelectTrigger className="border-calm-border rounded-2xl text-sm font-light">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="N/A">N/A</SelectItem>
                          <SelectItem value="15">15%</SelectItem>
                          <SelectItem value="7">7%</SelectItem>
                          <SelectItem value="2">2%</SelectItem>
                          <SelectItem value="1">1%</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormField>

                    <FormField label="Separador decimal (en ambigüedad)">
                      <Select value={formData.separador_decimal} onValueChange={(v) => updateField('separador_decimal', v)}>
                        <SelectTrigger className="border-calm-border rounded-2xl text-sm font-light">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="default">Funcionamiento por defecto</SelectItem>
                          <SelectItem value="punto">Punto (.)</SelectItem>
                          <SelectItem value="coma">Coma (,)</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormField>

                    <FormField label="Autorrellenar fecha de vencimiento">
                      <Select value={formData.autorrellenar_vencimiento} onValueChange={(v) => updateField('autorrellenar_vencimiento', v)}>
                        <SelectTrigger className="border-calm-border rounded-2xl text-sm font-light">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="N/A">N/A</SelectItem>
                          <SelectItem value="30">30 días</SelectItem>
                          <SelectItem value="60">60 días</SelectItem>
                          <SelectItem value="90">90 días</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormField>

                    <FormField label="Total factura mínimo esperado">
                      <Input
                        type="text"
                        value={formData.total_factura_minimo}
                        onChange={(e) => updateField('total_factura_minimo', e.target.value)}
                        className="font-mono border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light"
                        placeholder="0,00 €"
                      />
                    </FormField>

                    <FormField label="Total factura máximo esperado">
                      <Input
                        type="text"
                        value={formData.total_factura_maximo}
                        onChange={(e) => updateField('total_factura_maximo', e.target.value)}
                        className="font-mono border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light"
                        placeholder="0,00 €"
                      />
                    </FormField>

                    <FormField label="Actividad por Defecto">
                      <Input
                        type="text"
                        value={formData.actividad_defecto}
                        onChange={(e) => updateField('actividad_defecto', e.target.value)}
                        className="border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light"
                        placeholder="Actividad por defecto"
                      />
                    </FormField>

                    <FormField label="Cuenta Suplidos">
                      <Input
                        type="text"
                        value={formData.cuenta_suplidos}
                        onChange={(e) => updateField('cuenta_suplidos', e.target.value)}
                        className="font-mono border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light"
                        placeholder="Cuenta suplidos"
                      />
                    </FormField>

                    <FormField label="Recortar número de factura">
                      <Select value={formData.recortar_factura} onValueChange={(v) => updateField('recortar_factura', v)}>
                        <SelectTrigger className="border-calm-border rounded-2xl text-sm font-light">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="N/A">N/A</SelectItem>
                          <SelectItem value="inicio">Recortar desde inicio</SelectItem>
                          <SelectItem value="final">Recortar desde final</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormField>

                    <FormField label="Nº de caracteres a mantener">
                      <Input
                        type="text"
                        value={formData.caracteres_mantener}
                        onChange={(e) => updateField('caracteres_mantener', e.target.value)}
                        className="font-mono border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light"
                        placeholder="Ej: 8"
                      />
                    </FormField>

                    <div className="md:col-span-2">
                      <FormField label="Caracteres a excluir al recortar">
                        <Input
                          type="text"
                          value={formData.caracteres_excluir}
                          onChange={(e) => updateField('caracteres_excluir', e.target.value)}
                          className="border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light"
                          placeholder="Caracteres a excluir"
                        />
                      </FormField>
                    </div>
                  </div>

                  <Separator className="bg-calm-border my-6" />

                  {/* Checkbox — Ignorar Validación CIF */}
                  <div className="flex items-center gap-3">
                    <Checkbox
                      id="ignorar-validacion-cif-prov"
                      checked={formData.ignorar_validacion_cif}
                      onCheckedChange={(checked) => updateField('ignorar_validacion_cif', !!checked)}
                    />
                    <Label htmlFor="ignorar-validacion-cif-prov" className="cursor-pointer text-sm font-light text-calm-text">
                      Ignorar Validación CIF
                    </Label>
                  </div>
                </div>

                {/* Posición Fiscal */}
                <div className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <Icon name="gavel" className="text-calm-accent text-lg" />
                    <h3 className="text-sm font-display font-light text-calm-text">Posición Fiscal</h3>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[
                      { key: 'inversion_sujeto_pasivo_iva' as const, label: 'Inversión sujeto pasivo IVA' },
                      { key: 'inversion_sujeto_pasivo_igic' as const, label: 'Inversión sujeto pasivo IGIC' },
                      { key: 'intracomunitario' as const, label: 'Intracomunitario' },
                      { key: 'no_sujeta' as const, label: 'No sujeta' },
                      { key: 'tipo_cero' as const, label: 'Tipo 0%' },
                    ].map(({ key, label }) => (
                      <div
                        key={key}
                        className="flex items-center gap-3 p-4 rounded-xl border border-calm-border bg-calm-bg hover:bg-slate-50 transition-colors"
                      >
                        <Checkbox
                          id={`fiscal-${key}`}
                          checked={formData[key]}
                          onCheckedChange={(checked) => updateField(key, !!checked)}
                        />
                        <Label htmlFor={`fiscal-${key}`} className="cursor-pointer text-sm font-light text-calm-text">
                          {label}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Deducibilidad */}
                <div className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <Icon name="receipt_long" className="text-calm-accent text-lg" />
                    <h3 className="text-sm font-display font-light text-calm-text">Deducibilidad</h3>
                  </div>

                  <RadioGroup
                    value={formData.deducibilidad}
                    onValueChange={(v) => updateField('deducibilidad', v)}
                    className="space-y-3"
                  >
                    {[
                      { value: 'deducible', label: 'IVA/IGIC deducible (por defecto)' },
                      { value: 'no_deducible', label: 'IVA/IGIC no deducible' },
                      { value: 'parcial', label: 'IVA deducible parcialmente' },
                    ].map(({ value, label }) => (
                      <div
                        key={value}
                        className="flex items-center gap-3 p-4 rounded-xl border border-calm-border bg-calm-bg hover:bg-slate-50 cursor-pointer transition-colors"
                      >
                        <RadioGroupItem value={value} id={`deducibilidad-${value}`} />
                        <Label htmlFor={`deducibilidad-${value}`} className="cursor-pointer text-sm font-light text-calm-text">
                          {label}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              </div>
            </TabsContent>
          </Tabs>

        </div>
      </div>
    </Layout>
  );
}

// ─── Reusable Form Field ─────────────────────────────────────

interface FormFieldProps {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}

function FormField({ label, required, children }: FormFieldProps) {
  return (
    <div className="space-y-2">
      <Label className="text-[10px] uppercase tracking-widest text-calm-accent font-normal">
        {label}
        {required && <span className="text-red-400 ml-0.5">*</span>}
      </Label>
      {children}
    </div>
  );
}
